// @ts-nocheck - untyped
import React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme, alpha } from '@mui/material/styles';
import '../ServicesMonitor.scss';

function getOverviewHealthStatus(health, loading, error) {
  if (loading) return { label: 'Checking…', color: 'default', healthy: false };
  if (error) return { label: 'Error', color: 'error', healthy: false };
  const s = (health?.status || '').toUpperCase();
  if (s === 'UP') return { label: 'Healthy', color: 'success', healthy: true };
  if (s === 'DOWN' || s === 'OUT_OF_SERVICE')
    return { label: 'Error', color: 'error', healthy: false };
  return { label: 'Unknown', color: 'default', healthy: false };
}

function getAvailabilityPercent(health, loading, error) {
  if (loading || error) return 0;
  const s = (health?.status || '').toUpperCase();
  if (s === 'UP') return 100;
  if (s === 'DOWN' || s === 'OUT_OF_SERVICE') return 0;
  return 50;
}

export default function OverviewTab({
  services = [],
  servicesData = {},
  onServiceClick,
  onRefresh,
  loading = false,
}) {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const header = (
    <div className="sm-overview-header">
      <Typography variant="h4" className="sm-overview-title" sx={{ fontWeight: 'bold' }}>
        Services Overall Status
      </Typography>
      {onRefresh && (
        <Tooltip title="Refresh now">
          <IconButton
            onClick={onRefresh}
            disabled={loading}
            size="small"
            aria-label="Refresh data"
            style={{ color: loading ? alpha(ACCENT, 0.3) : ACCENT }}
          >
            <RefreshIcon className={loading ? 'rotate' : ''} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  if (!services || !services.length) {
    return (
      <Paper className="sm-overview-paper" elevation={0}>
        {header}
        <Typography variant="body1" color="text.secondary" className="sm-overview-empty">
          No services configured
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className="sm-overview-paper" elevation={0}>
      {header}

      <div className="sm-service-grid">
        {services.map((service, idx) => {
          const data = servicesData[service.id] || {};
          const status = getOverviewHealthStatus(data.health, data.healthLoading, data.healthError);
          const avail = getAvailabilityPercent(data.health, data.healthLoading, data.healthError);
          return (
            <Paper
              key={service.id}
              className="sm-service-card"
              elevation={0}
              onClick={() => onServiceClick(idx)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onServiceClick(idx);
                }
              }}
              aria-label={`${service.name}, ${status.label}, ${avail}% availability. Click for details.`}
            >
              <Typography
                variant="subtitle1"
                className="sm-service-card-name"
                noWrap
                title={service.name}
              >
                {service.name}
              </Typography>
              <div className="sm-service-card-status">
                {status.label === 'Checking…' && (
                  <CircularProgress size={14} style={{ color: ACCENT }} />
                )}
                {status.healthy && <CheckCircleOutlineIcon className="sm-icon-success" />}
                {status.color === 'error' && <ErrorOutlineIcon className="sm-icon-error" />}
                {status.label === 'Unknown' && <InfoOutlinedIcon className="sm-icon-info" />}
                <Typography variant="caption" className="sm-service-card-status-label">
                  {status.label === 'Unknown' ? 'Advisory' : status.label}
                </Typography>
              </div>
              <div className="sm-service-card-progress">
                <LinearProgress
                  variant="determinate"
                  value={avail}
                  className={`sm-progress-bar ${avail >= 100 ? 'sm-progress-success' : avail <= 0 ? 'sm-progress-error' : 'sm-progress-accent'}`}
                />
                <Typography
                  variant="caption"
                  className="sm-service-card-avail"
                  style={{
                    color:
                      avail >= 100
                        ? theme.tokens.success
                        : avail <= 0
                          ? theme.tokens.danger
                          : ACCENT,
                  }}
                >
                  {avail}%
                </Typography>
              </div>
            </Paper>
          );
        })}
      </div>
    </Paper>
  );
}
